import { observer, useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import StaticPageLayout from '../../../components/layouts/StaticPageLayout';
import OnboardingSteps from './agent/OnboardingSteps';
import { SEARCH_FIELDS } from '../../../utils/constants/fields';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { useStore } from '../../../store/store';
import useHistory from '../../../hooks/useHistory';
import { eventSearchTabSwitched } from '../../../ga4/ga4';
import MultiSelect from '../../../components/inputs/MultiSelect';
import ProfilerSearch from './profiler/ProfilerSearch';
import ProfilerIndividualsSearch from './profiler/ProfilerIndividualsSearch';
import ProfilerHowToPopup from './profiler/shared/ProfilerHowToPopup';
import FundsSearch from './funds/FundsSearch';
import PortfolioSearch from './portfolio/PortfolioSearch';
import SearchTabsNavigation from './search-tabs/SearchTabsNavigation';
import {
  isMMSearchDisabled,
  isIndividualsSearchDisabled
} from '../../../utils/constants/variables';

/**
 * AgentView - Tab orchestrator for search functionality
 *
 * Manages:
 * - Tab navigation between search types (Corporates, Individuals, Funds, Portfolio Co.)
 * - How-to popup display
 * - Company details view when a company is selected
 * - Mobile filter state coordination for StaticPageLayout
 *
 * Each search type delegates to a self-contained component that manages its own state.
 */
const AgentView = observer(({ isFromInvestorView = false }) => {
  const { profilerAccessStore, utilsStore } = useStore();
  const { location } = useHistory();

  // TD-1709: Read URL parameters for tab, category, and search query restoration
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab');
  const searchQueryFromUrl = urlParams.get('q') || '';
  const categoryFromUrl = urlParams.get('cat') || '';

  // Determine which tab the URL search query belongs to
  // Default tab (no ?tab param) is Profiler (Corporates)
  const urlTabValue = tabFromUrl || UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler;

  // Valid tab values from URL
  const validTabs = Object.values(UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]);
  const initialTab =
    tabFromUrl && validTabs.includes(tabFromUrl)
      ? tabFromUrl
      : UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler;

  const state = useLocalObservable(() => ({
    // Selected search type tab (initialized from URL or default)
    selectedTab: initialTab,
    setSelectedTab: (value) => {
      if (value !== state.selectedTab) {
        eventSearchTabSwitched(state.selectedTab, value);
      }
      state.selectedTab = value;
      // TD-1709: Update URL with new tab (replaceState to not pollute history)
      const params = new URLSearchParams(window.location.search);
      if (value === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler) {
        // Remove tab param for default tab
        params.delete('tab');
      } else {
        params.set('tab', value);
      }
      // Clear search query when switching tabs
      params.delete('q');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    },

    // How-to popup
    showProfilerHowToPopup: false,
    toggleProfilerHowToPopup: () => {
      state.showProfilerHowToPopup = !state.showProfilerHowToPopup;
      utilsStore.lockScroll(state.showProfilerHowToPopup);
    },

    // Mobile filter state (coordinated across search components)
    isMobileFiltersDisplayed: false,
    setMobileFiltersDisplayed: (value) => {
      state.isMobileFiltersDisplayed = value;
    }
  }));

  // localStorage effect for popup
  useEffect(() => {
    if (state.showProfilerHowToPopup) {
      localStorage.setItem('profiler-how-to-popup', 'true');
    }
  }, [state.showProfilerHowToPopup]);

  // Initial popup display effect
  useEffect(() => {
    const showInitialProfilerHowToPopup =
      profilerAccessStore.canUseProfilerSearch && !localStorage.getItem('profiler-how-to-popup');
    if (showInitialProfilerHowToPopup) {
      state.toggleProfilerHowToPopup();
    }
  }, [state, profilerAccessStore]);

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      if (state.showProfilerHowToPopup) {
        utilsStore.lockScroll(false);
      }
    };
  }, [state, utilsStore]);

  // Document title effect
  useEffect(() => {
    document.title = `transact.digital GmbH - AI-powered private market intelligence`;
  }, []);

  // Tab configuration
  const tabs = [
    {
      id: 'agent-view-profiler-tab',
      name: 'Corporates',
      value: UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler,
      isSelected: state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler,
      setTabValue: () => state.setSelectedTab(UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler),
      disabled: !profilerAccessStore.canUseProfilerSearch,
      tooltipContent: !profilerAccessStore.canUseProfilerSearch ? (
        <div className="tooltip-content-disabled-profiler-corporates-tab">
          Need access to corporate company profiles?
          <br />
          Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
          assistance.
        </div>
      ) : null
    },
    {
      id: 'agent-view-individuals-tab',
      name: 'Individuals',
      value: UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals,
      isSelected: state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals,
      setTabValue: () => state.setSelectedTab(UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals),
      disabled: isIndividualsSearchDisabled || !profilerAccessStore.canUseProfilerSearch,
      tooltipContent: isIndividualsSearchDisabled ? (
        <div className="tooltip-content-disabled-profiler-corporates-tab">Upcoming feature</div>
      ) : !profilerAccessStore.canUseProfilerSearch ? (
        <div className="tooltip-content-disabled-profiler-corporates-tab">
          Need access to individual profiles?
          <br />
          Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
          assistance.
        </div>
      ) : null
    },
    ...(!isFromInvestorView
      ? [
          {
            id: 'agent-view-funds-tab',
            name: 'Funds',
            value: UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds,
            isSelected: state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds,
            setTabValue: () => state.setSelectedTab(UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds),
            disabled: isMMSearchDisabled,
            tooltipContent: isMMSearchDisabled ? (
              <div className="tooltip-content-disabled-profiler-corporates-tab">
                Upcoming feature
              </div>
            ) : null
          },
          {
            id: 'agent-view-portfolios-tab',
            name: 'Portfolio companies',
            value: UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'],
            isSelected:
              state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'],
            setTabValue: () =>
              state.setSelectedTab(UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']),
            disabled: isMMSearchDisabled,
            tooltipContent: isMMSearchDisabled ? (
              <div className="tooltip-content-disabled-profiler-corporates-tab">
                Upcoming feature
              </div>
            ) : null
          }
        ]
      : []),
    ...([].includes(import.meta.env.VITE_ENVIRONMENT)
      ? [
          {
            id: 'agent-view-corporates-tab',
            name: 'Corporates',
            value: UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Corporates,
            isSelected: state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Corporates,
            setTabValue: () =>
              state.setSelectedTab(UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Corporates),
            disabled: isMMSearchDisabled,
            tooltipContent: isMMSearchDisabled ? (
              <div className="tooltip-content-disabled-profiler-corporates-tab">
                Upcoming feature
              </div>
            ) : null
          }
        ]
      : [])
  ];

  const desktopTabs = <SearchTabsNavigation tabs={tabs} />;

  // Mobile search type input
  const searchTypeInput = (
    <MultiSelect
      field={SEARCH_FIELDS.SEARCH_TYPE_MOBILE}
      value={[state.selectedTab]}
      setFieldValue={(field, value) => state.setSelectedTab(value[0])}
      values={tabs.filter(({ disabled }) => !disabled).map(({ name, value }) => ({ name, value }))}
    />
  );

  // Determine selected tab type
  const isFundSearch = state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds;
  const isPortfolioSearch =
    state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'];
  const isCorporatesSearch =
    state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Corporates;
  const isProfilerSearch = state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler;
  const isIndividualsSearch =
    state.selectedTab === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals;

  // Page title based on selected search type
  const title =
    isFundSearch || isPortfolioSearch || isCorporatesSearch
      ? 'Create your list of potential buyers'
      : isProfilerSearch
        ? 'Find relevant companies and insights'
        : isIndividualsSearch
          ? 'Find relevant individuals'
          : 'Unknown search';

  // TD-1709: Company and individual details now use URL-based navigation (standalone pages)
  // No inline overlay needed - navigation is handled by search components

  return (
    <>
      {/* flex: 1 + display:flex needed to participate in app-wrapper flex layout (TD-1776) */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}
      >
        <StaticPageLayout
          page="search-form"
          component={state.isMobileFiltersDisplayed ? 'mobile-filter' : 'general'}
        >
          <ProfilerHowToPopup
            isOpen={state.showProfilerHowToPopup}
            onClose={state.toggleProfilerHowToPopup}
          />
          <div className="search-container by-agent-default">
            <h1 key={title} className="form-title">
              {title}
            </h1>
            <div className="search-fields-container">
              {isFundSearch && (
                <FundsSearch
                  tabs={desktopTabs}
                  isFromInvestorView={isFromInvestorView}
                  onMobileFilterChange={state.setMobileFiltersDisplayed}
                />
              )}
              {isPortfolioSearch && (
                <PortfolioSearch tabs={desktopTabs} searchTypeInput={searchTypeInput} />
              )}
              {isProfilerSearch && (
                <ProfilerSearch
                  tabs={desktopTabs}
                  searchTypeInput={searchTypeInput}
                  onHowToPopupToggle={state.toggleProfilerHowToPopup}
                  onMobileFilterChange={state.setMobileFiltersDisplayed}
                  initialSearchQuery={
                    urlTabValue === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
                      ? searchQueryFromUrl
                      : ''
                  }
                  initialCategory={categoryFromUrl}
                />
              )}
              {isIndividualsSearch && (
                <ProfilerIndividualsSearch
                  tabs={desktopTabs}
                  searchTypeInput={searchTypeInput}
                  initialSearchQuery={
                    urlTabValue === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals
                      ? searchQueryFromUrl
                      : ''
                  }
                />
              )}
            </div>
            {!isFromInvestorView && (
              <div className="onboarding-container">
                <OnboardingSteps />
              </div>
            )}
          </div>
        </StaticPageLayout>
      </div>
    </>
  );
});

export default AgentView;
